import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useSuspenseQuery } from '@tanstack/react-query';
import {
    createColumnHelper,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { Badge } from '@givebutter/primitives/badge';
import { Box } from '@givebutter/primitives/box';
import { maskDecimal } from '@givebutter/util';
import { faUsers } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FetchWorkflowLogsParams, fetchWorkflowLogs } from 'api/workflows';
import * as Input from 'components/input';
import * as Table from 'components/table';
import * as Tooltip from 'components/tooltip';
import { EmptyData } from 'components/util';
import { ContactType } from 'constants/contacts';
import { CurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentWorkflow } from 'customHooks/useCurrentWorkflow';
import useDebounce from 'customHooks/useDebounce';
import { useIsPending } from 'customHooks/useIsPending';
import { usePersistentSearch } from 'customHooks/usePersistentSearch';
import { isoDateFromServer } from 'helpers/dates';
import { DateTime } from 'luxon';
import { workflowQueryKeys } from 'queries/workflow-query-keys';
import { WorkflowLog, WorkflowLogStatus } from 'types/domain';
import { PaginatedData } from 'types/pagination';

const columnHelper = createColumnHelper<WorkflowLog>();

const WorkflowDetailsActivityTable = () => {
    const { accountId, timezone } = useCurrentAccount();
    const { data: workflow } = useCurrentWorkflow();
    const [search, setSearch] = usePersistentSearch();
    const debouncedSearch = useDebounce(search);

    const {
        pagination,
        setPagination,
        expanded,
        setExpanded,
        sorting,
        setSorting,
        columnVisibility,
        setColumnVisibility,
    } = Table.useReactTableState({
        sortingState: [{ id: 'created_at', desc: true }],
        columnVisibilityState: {},
    });

    const statuses: WorkflowLogStatus[] = ['succeeded', 'delayed', 'failed'];
    const [isPending, params] = useIsPending<FetchWorkflowLogsParams>(
        {
            page: pagination.pageIndex + 1,
            query: debouncedSearch,
            statuses: statuses,
            per_page: pagination.pageSize,
            ...(sorting.length > 0
                ? { [`sortBy${sorting[0].desc ? 'Desc' : ''}`]: sorting[0].id }
                : {}),
        },
        500,
    );

    const { data: workflowLogs } = useSuspenseQuery<PaginatedData<WorkflowLog>>({
        queryKey: workflowQueryKeys.workflowLogsWithFilters(accountId, workflow.id, params),
        queryFn: () => fetchWorkflowLogs(accountId, workflow.id, params),
    });

    const columns = useMemo(
        () => [
            columnHelper.accessor('contact', {
                id: 'contact',
                enableSorting: true,
                enableHiding: false,
                meta: {
                    sortType: 'alpha',
                },
                header: 'Name',
                cell: info => {
                    const contact = info.getValue();
                    if (!contact) return 'Deleted or Archived Contact';

                    return (
                        <Link
                            to={`/accounts/${accountId}/${contact.type === ContactType.Company ? 'companies' : 'contacts'}/${contact.id}`}
                            className="font-medium text-black hover:underline"
                        >
                            {contact.type === ContactType.Company
                                ? contact.company_name
                                : contact.name_display}
                        </Link>
                    );
                },
            }),
            columnHelper.accessor('created_at', {
                id: 'created_at',
                header: 'Triggered',
                enableSorting: true,
                meta: {
                    sortType: 'date',
                },
                cell: info => {
                    const value = info.getValue();
                    return value
                        ? isoDateFromServer(value)
                              .setZone(timezone)
                              .toLocaleString(DateTime.DATETIME_FULL)
                        : 'Never';
                },
            }),
            columnHelper.accessor('revenue', {
                id: 'revenue',
                header: 'Revenue generated',
                enableSorting: true,
                meta: {
                    sortType: 'numeric',
                },
                cell: info => {
                    const value = info.getValue();
                    return value ? (
                        <>
                            <CurrencySymbol />
                            {maskDecimal(value)}
                        </>
                    ) : (
                        <EmptyData />
                    );
                },
            }),
            columnHelper.accessor('status', {
                id: 'status',
                header: 'Status',
                enableSorting: false,
                cell: info => {
                    const status = info.getValue();
                    switch (status) {
                        case 'succeeded':
                            return <Badge color="success">Completed</Badge>;
                        case 'failed':
                            return <Badge color="error">Failed</Badge>;
                        case 'started':
                            return <Badge>Running</Badge>;
                        case 'delayed':
                            return (
                                <Tooltip.Root>
                                    <Tooltip.Trigger asChild>
                                        <Badge>Waiting</Badge>
                                    </Tooltip.Trigger>
                                    <Tooltip.Portal>
                                        <Tooltip.Content side="bottom" align="end" inverse>
                                            <Tooltip.Arrow inverse />
                                            <p className="max-w-36 text-sm">
                                                Workflow has been triggered, waiting to execute
                                                delayed action.
                                            </p>
                                        </Tooltip.Content>
                                    </Tooltip.Portal>
                                </Tooltip.Root>
                            );
                    }
                    return null;
                },
            }),
        ],
        [timezone, accountId],
    );

    const tableState = {
        pagination,
        expanded,
        sorting,
        columnVisibility,
    };

    const table = useReactTable({
        data: workflowLogs.data,
        columns,
        pageCount: workflowLogs?.meta.last_page ?? -1,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        initialState: tableState,
        state: tableState,
        onPaginationChange: setPagination,
        onExpandedChange: setExpanded,
        onSortingChange: setSorting,
        onColumnVisibilityChange: setColumnVisibility,
        manualPagination: true,
        manualSorting: true,
    });

    return (
        <Table.Root variant="bordered" table={table} searchTerm={search} isLoading={isPending}>
            <Box>
                <div className="flex flex-col gap-4 border-b px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex flex-col">
                        <h2 className="text-xl font-medium">Activity</h2>
                        <p className="text-base text-gray-500">
                            Contacts that have entered your workflow
                        </p>
                    </div>
                    <div className="flex items-center gap-4">
                        <Input.Root size={'sm'}>
                            <Input.SearchIcon />
                            <Input.Form
                                placeholder={'Search'}
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                        </Input.Root>
                        <div className="flex gap-1">
                            <Table.ColumnVisibility
                                transformIdToReadable={id => {
                                    switch (id) {
                                        case 'contact':
                                            return 'Name';
                                        case 'created_at':
                                            return 'Triggered at';
                                        case 'revenue':
                                            return 'Revenue generated';
                                        default:
                                            return id;
                                    }
                                }}
                            />
                            <Table.ToggleFullWidth />
                        </div>
                    </div>
                </div>
                <div className="flex flex-col gap-y-4">
                    <div className="overflow-x-auto">
                        <Table.Content>
                            <Table.Header />
                            <Table.Body />
                        </Table.Content>
                        <Table.Empty variant="primary" className="max-w-full">
                            <Table.EmptyIcon>
                                <FontAwesomeIcon icon={faUsers} />
                            </Table.EmptyIcon>
                            <Table.EmptyTitle>
                                No one has received this workflow yet
                            </Table.EmptyTitle>
                            <Table.EmptyDescription>
                                When a contact has received your workflow, we’ll show them here.
                            </Table.EmptyDescription>
                        </Table.Empty>
                    </div>
                </div>
                <Table.Pagination className="border-t border-t-gray-100 p-2">
                    <Table.PaginationPageNavigation />
                    <Table.PaginationPerPageDropdown />
                </Table.Pagination>
            </Box>
        </Table.Root>
    );
};

export { WorkflowDetailsActivityTable };
